/**
 * Agent-approval analytics — Zod schemas (the governance dashboard aggregate).
 *
 * Server-aggregated; the dashboard never sums the row list client-side. Field
 * names match the backend (approvaladmin /approval-analytics) verbatim.
 */

import { z } from "zod";

export const approvalTotalsSchema = z.object({
  total: z.number().int().nonnegative(),
  // approved INCLUDES 'consumed' (the normal terminal state once the agent acts on its
  // approval) — one "approved" definition shared by the funnel, trend, and per-agent
  // breakdown so the dashboard never shows two different numbers. total = approved +
  // denied + expired + pending.
  approved: z.number().int().nonnegative(),
  denied: z.number().int().nonnegative(),
  expired: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
  // The old money "value-at-risk" sum is intentionally GONE: in the generic consequence model a
  // held action carries a list of (dimension, quantity) measurements with no single scalar amount
  // (usd + iocs + shares cannot be summed into one number). The backend no longer emits it; a
  // per-dimension value rollup is a tracked dashboard follow-up. The funnel counts stay honest.
});
export type ApprovalTotals = z.infer<typeof approvalTotalsSchema>;

export const agentApprovalCountSchema = z.object({
  agent_id: z.string(),
  agent_name: z.string().nullable(),
  total: z.number().int().nonnegative(),
  approved: z.number().int().nonnegative(),
  denied: z.number().int().nonnegative(),
});
export type AgentApprovalCount = z.infer<typeof agentApprovalCountSchema>;

export const approvalDayCountSchema = z.object({
  day: z.string(), // RFC3339 (UTC day boundary)
  approved: z.number().int().nonnegative(),
  denied: z.number().int().nonnegative(),
  expired: z.number().int().nonnegative(),
});
export type ApprovalDayCount = z.infer<typeof approvalDayCountSchema>;

export const approvalAnalyticsSchema = z.object({
  window: z.object({ since: z.string(), until: z.string() }),
  totals: approvalTotalsSchema,
  top_agents: z.array(agentApprovalCountSchema),
  trend: z.array(approvalDayCountSchema),
});
export type ApprovalAnalytics = z.infer<typeof approvalAnalyticsSchema>;

// Window presets the dashboard offers (days back from now). URL-driven via ?days.
export const analyticsWindowSchema = z.object({
  days: z.coerce.number().int().refine((n) => [7, 30, 90].includes(n)).optional(),
});
export type AnalyticsWindow = z.infer<typeof analyticsWindowSchema>;
